import { useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useAuthStore } from '../stores/useAuthStore';
import type { NotificationRecord } from '../types';

const POLL_INTERVAL = 30000;

export function useNotificationPolling() {
  const user = useAuthStore((s) => s.user);
  const lastCount = useRef<number | null>(null);

  useEffect(() => {
    if (!user) return;

    const poll = async () => {
      if (document.visibilityState !== 'visible') return;

      try {
        const data = await api<{ count: number }>('/notifications/unread-count');
        const store = useNotificationStore.getState();
        store.setUnreadCount(data.count);

        // If count changed, fetch new notifications
        if (lastCount.current !== null && data.count > lastCount.current) {
          const notifs = await api<NotificationRecord[]>('/notifications?unread=true');
          // Add any new ones not already in store
          const existingIds = new Set(store.notifications.map((n) => n.id));
          for (const n of notifs) {
            if (!existingIds.has(n.id)) {
              store.addNotification(n);
            }
          }
        }
        lastCount.current = data.count;
      } catch {
        // Silently fail
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);
}
